import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useParams, useNavigate } from 'react-router-dom'
import { Card } from '../components/Card'
import { Icon, type IconName } from '../components/Icon'
import { BarChart } from '../components/charts/BarChart'
import { ChartPeriodPicker, computePeriod } from '../components/ChartPeriodPicker'
import { useCurrency } from '../lib/currency-context'
import type { PeriodKey } from '../lib/period-context'
import { fmtMoney, FMT_INT, SYMBOL, FX } from '../lib/money'
import { useTranslation } from 'react-i18next'

interface CarrierListItem {
  code: string; name: string; color: string; icon: string; notes: string
  shipments: number; revenue_minor: number; cost_minor: number; profit_minor: number; margin_pct: number; status: string
}

interface CarrierDeep {
  carrier: { code: string; name: string; color: string; icon: string; notes: string }
  totals: { ships: number; revenue_minor: number; cost_minor: number; profit_minor: number; margin_pct: number }
  monthly: Array<{ month: string; ships: number; revenue_minor: number; cost_minor: number }>
  topCustomers: Array<{ name: string; revenue_minor: number; profit_minor: number; ships: number; margin_pct: number }>
  recon: { bl_total: number; matched: number; matched_pct: number; api_only: number; api_only_pct: number }
  leakageCategories: Array<{ type: string; minor: number }>
  extras: Record<string, number>
}

export function CarriersView() {
  const { t } = useTranslation()
  const { currency } = useCurrency()
  const navigate = useNavigate()
  const { code } = useParams<{ code?: string }>()

  // Each section owns its own period
  const [tabsPeriod,     setTabsPeriod]     = useState<PeriodKey>('ytd')
  const [carrierPeriod,  setCarrierPeriod]  = useState<PeriodKey>('ytd')
  const tabsPeriodRange    = computePeriod(tabsPeriod)
  const carrierPeriodRange = computePeriod(carrierPeriod)

  const { data: list } = useQuery<CarrierListItem[]>({
    queryKey: ['carriers', tabsPeriodRange.from, tabsPeriodRange.to],
    queryFn: () => fetch(`/api/carriers?from=${tabsPeriodRange.from}&to=${tabsPeriodRange.to}`).then(r => r.json()),
    staleTime: 60_000,
  })

  const selectedCode = code ?? list?.find(c => c.shipments > 0)?.code ?? 'usps'

  const { data, isLoading } = useQuery<CarrierDeep>({
    queryKey: ['carrier', selectedCode, carrierPeriodRange.from, carrierPeriodRange.to],
    queryFn: () => fetch(`/api/carriers/${selectedCode}?from=${carrierPeriodRange.from}&to=${carrierPeriodRange.to}`).then(r => r.json()),
    enabled: !!selectedCode,
  })

  const sym = SYMBOL[currency]
  const fxScale = FX[currency]

  return (
    <div>
      <div className="mb-10">
        <div className="text-[12px] font-semibold text-royal-600 uppercase tracking-wider">Module 4</div>
        <h1 className="text-[32px] font-bold tracking-tight mt-2">{t('carriers.title')}</h1>
        <p className="text-ink-500 text-[14px] mt-2">{t('carriers.subtitle')}</p>
      </div>

      <div className="flex items-end justify-between mb-3">
        <div>
          <div className="text-[16px] font-semibold tracking-tight">{t('carriers.pick_a_carrier')}</div>
          <div className="text-[12.5px] text-ink-500 mt-1">{t('carriers.pick_sub')}</div>
        </div>
        <ChartPeriodPicker value={tabsPeriod} onChange={setTabsPeriod} />
      </div>

      <Card className="mb-10 p-2 overflow-x-auto scrollbar-none">
        <div className="flex items-center gap-1.5 min-w-max">
          {list?.map(c => {
            const active = c.code === selectedCode
            return (
              <button
                key={c.code}
                onClick={() => navigate(`/carriers/${c.code}`)}
                className={`h-10 px-3 inline-flex items-center gap-2 rounded-lg text-[13px] font-semibold ${active ? 'text-white' : 'text-ink-700 hover:bg-ink-50'}`}
                style={active ? { background: c.color } : {}}
              >
                <Icon name={c.icon as IconName} /> {c.name}
              </button>
            )
          })}
        </div>
      </Card>

      {isLoading || !data ? (
        <div className="p-8 text-ink-500">{t('carriers.loading_carrier')}</div>
      ) : (
        <>
          <Card className="p-5 mb-5 relative overflow-hidden">
            <div className="absolute inset-0 opacity-[.07]" style={{ background: `radial-gradient(80% 80% at 100% 0%, ${data.carrier.color}, transparent 60%)` }} />
            <div className="relative flex items-center gap-5">
              <div className="w-14 h-14 rounded-2xl grid place-items-center text-white shadow-md" style={{ background: data.carrier.color }}>
                <Icon name={data.carrier.icon as IconName} className="w-7 h-7" />
              </div>
              <div className="flex-1">
                <h2 className="text-[22px] font-bold tracking-tight">{data.carrier.name}</h2>
                <div className="text-[13px] text-ink-500 mt-1">{data.carrier.notes}</div>
              </div>
              <div className="grid grid-cols-4 gap-3 text-right">
                <Metric label="Shipments YTD" value={FMT_INT.format(data.totals.ships)} />
                <Metric label="Revenue"       value={fmtMoney(data.totals.revenue_minor, currency, { compact: true })} />
                <Metric label="Cost"          value={fmtMoney(data.totals.cost_minor,    currency, { compact: true })} />
                <Metric label="Margin"        value={`${data.totals.margin_pct.toFixed(1)}%`} valueClass="text-mint-600" />
              </div>
            </div>
          </Card>

          <div className="grid grid-cols-12 gap-4 mb-5">
            <Card className="col-span-8 p-5">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="text-[15px] font-semibold">{t('carriers.monthly_pl')} · {data.carrier.name}</div>
                  <div className="text-[12px] text-ink-500">{carrierPeriodRange.from} → {carrierPeriodRange.to}</div>
                </div>
                <ChartPeriodPicker value={carrierPeriod} onChange={setCarrierPeriod} />
              </div>
              {data.monthly.length > 0 ? (
                <BarChart
                  labels={data.monthly.map(m => m.month)}
                  data={data.monthly.map(m => m.revenue_minor / 100 * fxScale)}
                  color={data.carrier.color}
                  height={260}
                  yTickFormat={(v) => sym + (v / 1000).toFixed(0) + 'K'}
                  barThickness={24}
                />
              ) : (
                <div className="h-[260px] grid place-items-center text-[13px] text-ink-500">No shipments YTD for this carrier.</div>
              )}
            </Card>

            <Card className="col-span-4 p-5">
              <div className="text-[15px] font-semibold mb-1">Reconciliation</div>
              <div className="text-[12px] text-ink-500 mb-3">ByeLabel labels vs carrier invoice</div>
              <div className="space-y-3">
                {[
                  { lbl: 'Matched (invoiced)',        v: data.recon.matched_pct,                                color: 'bg-mint-500'  },
                  { lbl: 'API-only (no invoice yet)', v: data.recon.api_only_pct,                              color: 'bg-flame-500' },
                  { lbl: 'Carrier-only (blind spot)', v: Math.max(0, 100 - data.recon.matched_pct - data.recon.api_only_pct), color: 'bg-honey-500' },
                ].map(r => (
                  <div key={r.lbl}>
                    <div className="flex items-center justify-between text-[12.5px] mb-1">
                      <span>{r.lbl}</span>
                      <span className="num font-semibold">{r.v.toFixed(1)}%</span>
                    </div>
                    <div className="h-2 rounded-full bg-ink-100 overflow-hidden">
                      <div className={`h-full rounded-full ${r.color}`} style={{ width: `${Math.min(r.v, 100)}%` }} />
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-4 pt-3 border-t border-ink-100">
                <div className="text-[12px] text-ink-500">Tracking numbers (ByeLabel)</div>
                <div className="text-[14px] font-semibold mt-1">{FMT_INT.format(data.recon.bl_total)} total · {FMT_INT.format(data.recon.matched)} matched</div>
              </div>
            </Card>
          </div>

          <CarrierSpecial code={data.carrier.code} extras={data.extras} carrier={data.carrier} />

          <div className="grid grid-cols-12 gap-4 mb-5">
            <Card className="col-span-7 p-5">
              <div className="text-[15px] font-semibold mb-1">Leakage categories · YTD</div>
              <div className="text-[12px] text-ink-500 mb-3">Charges flagged for dispute</div>
              {data.leakageCategories.length > 0 ? (
                <BarChart
                  labels={data.leakageCategories.map(l => l.type)}
                  data={data.leakageCategories.map(l => l.minor)}
                  colors={['#ef4444','#f59e0b','#6366f1','#0ea5e9','#a855f7','#10b981']}
                  horizontal
                  height={240}
                  xTickFormat={(v) => sym + ((v * fxScale) / 1000).toFixed(0) + 'K'}
                  barThickness={28}
                />
              ) : (
                <div className="h-[240px] grid place-items-center text-[13px] text-ink-500">
                  No leakage data yet for {data.carrier.name}.
                </div>
              )}
            </Card>

            <Card className="col-span-5 p-5">
              <div className="text-[15px] font-semibold mb-1">Top customers · {data.carrier.name}</div>
              <div className="text-[12px] text-ink-500 mb-3">By revenue · YTD</div>
              <div className="space-y-2.5">
                {data.topCustomers.length === 0 && <div className="text-[13px] text-ink-500">No customers yet.</div>}
                {data.topCustomers.map((c, i) => (
                  <div key={c.name} className="flex items-center gap-3 p-2 rounded-lg hover:bg-ink-50">
                    <div className="w-7 h-7 rounded-lg grid place-items-center text-[11px] font-bold bg-ink-100 text-ink-600 num">{i + 1}</div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[14px] font-semibold truncate">{c.name}</div>
                      <div className="text-[11.5px] text-ink-500">Margin {c.margin_pct.toFixed(1)}% · {FMT_INT.format(c.ships)} ships</div>
                    </div>
                    <div className="num font-semibold text-[13px]">{fmtMoney(c.revenue_minor, currency, { compact: true })}</div>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        </>
      )}
    </div>
  )
}

function Metric({ label, value, valueClass = '' }: { label: string; value: string; valueClass?: string }) {
  return (
    <div>
      <div className="text-[11px] uppercase text-ink-500 font-semibold">{label}</div>
      <div className={`num font-bold text-[20px] ${valueClass}`}>{value}</div>
    </div>
  )
}

function CarrierSpecial({ code, extras, carrier }: { code: string; extras: Record<string, number>; carrier: { color: string } }) {
  const { t } = useTranslation()
  const { currency } = useCurrency()
  if (code === 'dhl') {
    return (
      <div className="grid grid-cols-12 gap-4 mb-5">
        <Card className="col-span-6 p-5">
          <div className="text-[12px] font-semibold text-ink-500 uppercase tracking-wider">DHL split-billing</div>
          <div className="text-[15px] font-semibold mt-0.5 mb-3">PHL0 (transport) vs PHLI (destination)</div>
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-xl bg-honey-50 border border-honey-100 p-4">
              <div className="text-[11px] uppercase tracking-wider text-honey-700 font-semibold">PHL0 · Transport</div>
              <div className="num font-bold text-[22px] mt-1">{fmtMoney(extras.phl0_cost_minor ?? 0, currency, { compact: true })}</div>
            </div>
            <div className="rounded-xl bg-royal-50 border border-royal-100 p-4">
              <div className="text-[11px] uppercase tracking-wider text-royal-700 font-semibold">PHLI · Destination</div>
              <div className="num font-bold text-[22px] mt-1">{fmtMoney(extras.phli_cost_minor ?? 0, currency, { compact: true })}</div>
            </div>
          </div>
          <div className="mt-3 text-[12.5px] text-ink-600 bg-ink-50 rounded-lg p-3 border border-ink-100">
            <Icon name="info" className="w-4 h-4 inline -mt-0.5 text-ink-500" /> PHL0 / PHLI pairs are never treated as duplicates — collision detection is on (shipment, product) tuple only.
          </div>
        </Card>
        <Card className="col-span-6 p-5">
          <div className="text-[12px] font-semibold text-ink-500 uppercase tracking-wider">Dispute pipeline</div>
          <div className="text-[15px] font-semibold mt-0.5 mb-3">Pulls from dhl_disputes table</div>
          <div className="text-[12.5px] text-ink-600">Hook this card into <span className="mono">/api/disputes?carrier=dhl</span> for live counts.</div>
        </Card>
      </div>
    )
  }
  if (code === 'usps') {
    return (
      <div className="grid grid-cols-12 gap-4 mb-5">
        <Card className="col-span-12 p-5">
          <div className="text-[12px] font-semibold text-ink-500 uppercase tracking-wider">{t('carriers.mysys_passthrough')}</div>
          <div className="text-[15px] font-semibold mt-0.5 mb-3">{t('carriers.mysys_sub', { n: FMT_INT.format(extras.mysys_orphans ?? 0) })}</div>
          <div className="grid grid-cols-3 gap-3 max-w-2xl">
            <div className="rounded-xl bg-mint-50 border border-mint-100 p-4">
              <div className="text-[11px] uppercase tracking-wider text-mint-700 font-semibold">{t('carriers.mysys_matched')}</div>
              <div className="num font-bold text-[20px] mt-1">{FMT_INT.format(extras.mysys_matched ?? 0)}</div>
            </div>
            <div className="rounded-xl bg-ink-50 border border-ink-100 p-4">
              <div className="text-[11px] uppercase tracking-wider text-ink-700 font-semibold">{t('carriers.mysys_unverifiable')}</div>
              <div className="num font-bold text-[20px] mt-1">{FMT_INT.format(extras.mysys_unverifiable ?? 0)}</div>
            </div>
            <div className="rounded-xl bg-plum-50 border border-plum-100 p-4">
              <div className="text-[11px] uppercase tracking-wider text-plum-700 font-semibold">{t('carriers.mysys_unexplained')}</div>
              <div className="num font-bold text-[20px] mt-1">{FMT_INT.format(extras.mysys_unexplained ?? 0)}</div>
            </div>
          </div>
          <div className="text-[12px] text-ink-500 mt-3">{t('carriers.mysys_attr')}</div>
        </Card>
      </div>
    )
  }
  if (code === 'gofo') {
    return (
      <div className="grid grid-cols-12 gap-4 mb-5">
        <Card className="col-span-12 p-5 relative overflow-hidden">
          <div className="absolute top-4 right-4 pill pill-mint">Opportunity</div>
          <div className="text-[12px] font-semibold text-ink-500 uppercase tracking-wider">Concierge model · with Printanem (TX)</div>
          <div className="text-[15px] font-semibold mt-0.5 mb-3">Pricing: USPS Tier 4 sub-pound · GOFO DO + $1.50 over 1 lb</div>
          <div className="text-[12.5px] text-ink-600 bg-ink-50 rounded-lg p-3 border border-ink-100" style={{ borderColor: carrier.color + '33' }}>
            <Icon name="info" className="w-4 h-4 inline -mt-0.5 text-ink-500" /> Concierge projection lives in <span className="mono">exports/GOFO/</span>. Wire this card to the projection script for live uplift numbers.
          </div>
        </Card>
      </div>
    )
  }
  if (code === 'evri') {
    return (
      <div className="grid grid-cols-12 gap-4 mb-5">
        <Card className="col-span-12 p-5">
          <div className="text-[12px] font-semibold text-ink-500 uppercase tracking-wider">LDS · London Density Surcharge</div>
          <div className="text-[15px] font-semibold mt-0.5 mb-3">Period-level surcharge pro-rated across London parcels</div>
          <div className="grid grid-cols-4 gap-3">
            <div className="rounded-xl bg-mint-50 border border-mint-100 p-4">
              <div className="text-[11px] uppercase tracking-wider text-mint-700 font-semibold">Periods recorded</div>
              <div className="num font-bold text-[22px] mt-1">{extras.lds_period_count ?? 0}</div>
            </div>
            <div className="rounded-xl bg-ink-50 border border-ink-100 p-4">
              <div className="text-[11px] uppercase tracking-wider text-ink-700 font-semibold">Per-parcel adj</div>
              <div className="num font-bold text-[22px] mt-1">£0.75</div>
            </div>
            <div className="rounded-xl bg-royal-50 border border-royal-100 p-4">
              <div className="text-[11px] uppercase tracking-wider text-royal-700 font-semibold">Threshold</div>
              <div className="num font-bold text-[22px] mt-1">7.5%</div>
            </div>
            <div className="rounded-xl bg-honey-50 border border-honey-100 p-4">
              <div className="text-[11px] uppercase tracking-wider text-honey-700 font-semibold">Allocation</div>
              <div className="num font-bold text-[14px] mt-1 text-honey-700">Pro-rata</div>
            </div>
          </div>
        </Card>
      </div>
    )
  }
  return null
}
